import { useCallback, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { FileUpload, type FileUploadSelectEvent } from 'primereact/fileupload';
import { useDispatch } from 'react-redux';

import {
  freshStrawRestApi,
  useImportFreshStrawFreshEmbryosBatchMutation,
  useImportFreshStrawFreshEmbryosMutation,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useImportFreshEmbryosBatchV3Mutation } from '@/services/fresh-shipment';
import { isAbortError } from '@/services/progress-stream';

type UploadTransferPregCheckProps = {
  visible: boolean;
  setVisible: (visible: boolean) => void;
};

export const UploadTransferPregCheck = ({
  visible,
  setVisible,
}: UploadTransferPregCheckProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const fileUploadRef = useRef<FileUpload>(null);
  const useBatchUpload = useFeatureFlag('Batch_Upload_Fresh_Embryos');
  const isOverrideEnabled = useFeatureFlag(
    'Fresh_Shipment_Billable_Shipped_Update',
  );
  const isProgramNameEnabled = useFeatureFlag(
    'Fresh_Shipment_Export_Program_Name',
  );

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);

  const [importFreshEmbryos, { isLoading: isLoadingStandard }] =
    useImportFreshStrawFreshEmbryosMutation();
  const [importFreshEmbryosBatch, { isLoading: isLoadingBatch }] =
    useImportFreshStrawFreshEmbryosBatchMutation();
  const { mutateAsync: importFreshEmbryosV3, isPending: isLoadingV2 } =
    useImportFreshEmbryosBatchV3Mutation();

  const isLoading = isLoadingStandard || isLoadingBatch || isLoadingV2;

  const handleFileSelect = useCallback((event: FileUploadSelectEvent) => {
    setSelectedFile(event.files[0] ?? null);
    setUploadErrors([]);
  }, []);

  const resetForm = useCallback(() => {
    setSelectedFile(null);
    setUploadErrors([]);
    fileUploadRef.current?.clear();
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    try {
      const importMutation = useBatchUpload
        ? importFreshEmbryosBatch
        : importFreshEmbryos;
      const result = await importMutation({ file: selectedFile }).unwrap();

      const skippedCount = result.skipped ?? 0;
      if (result.errors.length > 0 || skippedCount > 0) {
        if (result.errors.length > 0) setUploadErrors(result.errors);
        dispatch(
          addToast({
            severity: 'warn',
            summary:
              result.errors.length > 0
                ? 'Upload Complete with Errors'
                : 'Upload Complete',
            detail: `Parsed: ${result.parsed}, Saved: ${result.saved}${skippedCount > 0 ? `, Skipped: ${skippedCount}` : ''}${result.errors.length > 0 ? `, Errors: ${result.errors.length}` : ''}`,
            life: ToastLife.LONG,
          }),
        );
        if (result.errors.length === 0) {
          resetForm();
          setVisible(false);
        }
      } else {
        dispatch(
          addToast({
            severity: 'success',
            summary: 'Upload Complete',
            detail: `Parsed: ${result.parsed}, Saved: ${result.saved}`,
            life: ToastLife.DEFAULT,
          }),
        );
        resetForm();
        setVisible(false);
      }
    } catch (err) {
      const raw = (err as { data?: string })?.data;
      let message = 'There was an error uploading the data.';
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw) as { message?: string };
          if (parsed.message) message = parsed.message;
        } catch {
          message = raw;
        }
      }
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Upload Failed',
          detail: message,
          life: ToastLife.LONG,
        }),
      );
    }
  }, [
    selectedFile,
    useBatchUpload,
    importFreshEmbryosBatch,
    importFreshEmbryos,
    dispatch,
    resetForm,
    setVisible,
  ]);

  const handleUploadV2 = useCallback(async () => {
    if (!selectedFile) return;

    try {
      const result = await importFreshEmbryosV3({ file: selectedFile });

      dispatch(
        freshStrawRestApi.util.invalidateTags([
          'FertilizationListFreshShipment',
        ]),
      );
      void queryClient.invalidateQueries({
        queryKey: ['fresh-straw', 'fertilizations', 'v2'],
      });

      const skippedCount = result.skipped ?? 0;
      const updatedCount = result.updated ?? 0;
      const hasWarnings = result.errors.length > 0 || skippedCount > 0;

      if (hasWarnings) {
        if (result.errors.length > 0) setUploadErrors(result.errors);

        const details: string[] = [
          `Parsed: ${result.parsed}`,
          `Saved: ${result.saved}`,
        ];
        if (updatedCount > 0) details.push(`Updated: ${updatedCount}`);
        if (skippedCount > 0) details.push(`Skipped: ${skippedCount}`);
        if (result.errors.length > 0)
          details.push(`Errors: ${result.errors.length}`);

        dispatch(
          addToast({
            severity: 'warn',
            summary:
              result.errors.length > 0
                ? 'Upload Complete with Errors'
                : 'Upload Complete',
            detail: details.join(', '),
            life: ToastLife.LONG,
          }),
        );
        if (result.errors.length === 0) {
          resetForm();
          setVisible(false);
        }
      } else {
        const details: string[] = [
          `Parsed: ${result.parsed}`,
          `Saved: ${result.saved}`,
        ];
        if (updatedCount > 0) details.push(`Updated: ${updatedCount}`);

        dispatch(
          addToast({
            severity: 'success',
            summary: 'Upload Complete',
            detail: details.join(', '),
            life: ToastLife.DEFAULT,
          }),
        );
        resetForm();
        setVisible(false);
      }
    } catch (err) {
      // The user hit Cancel on the progress toast, which already reported it.
      if (isAbortError(err)) return;

      const raw = (err as { data?: string })?.data;
      let message = 'There was an error uploading the data.';
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw) as { message?: string };
          if (parsed.message) message = parsed.message;
        } catch {
          message = raw;
        }
      }
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Upload Failed',
          detail: message,
          life: ToastLife.LONG,
        }),
      );
    }
  }, [
    selectedFile,
    importFreshEmbryosV3,
    dispatch,
    queryClient,
    resetForm,
    setVisible,
  ]);

  const handleUploadV2WithConfirm = useCallback(() => {
    if (!selectedFile) return;

    setIsOverrideDialogOpen(true);
  }, [selectedFile]);

  const handleOverrideConfirm = useCallback(() => {
    setIsOverrideDialogOpen(false);
    void handleUploadV2();
  }, [handleUploadV2]);

  const handleHide = useCallback(() => {
    resetForm();
    setVisible(false);
  }, [resetForm, setVisible]);

  const footerContent = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={handleHide}
        className="p-button-text"
      />
      <Button
        disabled={!selectedFile || isLoading}
        label="Upload"
        icon="pi pi-upload"
        onClick={
          isProgramNameEnabled || isOverrideEnabled
            ? handleUploadV2WithConfirm
            : handleUpload
        }
        loading={isLoading}
        autoFocus
      />
    </div>
  );

  return (
    <>
      <ConfirmDialog />
      <Dialog
        header="Upload Fresh Embryos"
        visible={visible}
        onHide={handleHide}
        style={{ width: '40vw' }}
        footer={footerContent}
      >
        <div className="flex flex-col gap-4">
          <FileUpload
            ref={fileUploadRef}
            name="file"
            mode="basic"
            accept=".xlsx,.xls,.csv"
            maxFileSize={20000000}
            chooseLabel="Choose File"
            onSelect={handleFileSelect}
          />

          {selectedFile && (
            <p className="text-sm text-green-600">
              {selectedFile.name} ready to upload.
            </p>
          )}

          {uploadErrors.length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded border border-red-300 bg-red-50 p-3">
              <p className="mb-1 text-sm font-medium text-red-700">
                Errors ({uploadErrors.length}):
              </p>
              <ul className="list-inside list-disc text-sm text-red-600">
                {uploadErrors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {isProgramNameEnabled && (
          <AlertDialog
            open={isOverrideDialogOpen}
            onOpenChange={setIsOverrideDialogOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Override Existing Data</AlertDialogTitle>
                <AlertDialogDescription>
                  If embryo data already exists, it will be overridden with the
                  uploaded values. Do you want to continue?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleOverrideConfirm}>
                  Upload &amp; Override
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </Dialog>
    </>
  );
};
